import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';

import { SplashScreen } from '../../components/SplashScreen';
import { useGraphQLQuery } from '../../hooks/useGraphQLQuery.hook';
import { Loading } from '../../components/Loading';
import { SaveToBookmarks } from '../../components/Illustrations/SaveToBookmarks';
import { FolderBusinessEntityList } from '../../components/FolderBusinessEntityList';
import { FolderBusinessEntityListVariant } from '../../components/FolderBusinessEntityList/enums/variant.enum';
import { PATH_COLLABORATION_MISSIONS_NEW } from '../../app/constants/paths.constant';
import { useBuildPath } from '../../hooks/useBuildPath.hook';
import { useIdParam } from '../../utils/use-id-param.util';

import { useSubMissionCreationGate } from './hooks/useSubMissionCreationGate.hook';

export const CollaborationMissionsPage_SubMissions_QueryDocument = graphql(`
  query CollaborationMissionsPage_SubMissions_Query($missionId: Id!) {
    findOneMission(id: $missionId) {
      _id
      subMissions {
        ...FolderBusinessEntityListCmp_FolderBusinessEntity_Fragment
      }
    }
  }
`);

export const SubMissions = () => {
  const navigate = useNavigate();

  const folderId = useIdParam('folderId');
  const missionId = useIdParam('missionId');

  const { data, isLoading } = useGraphQLQuery(
    CollaborationMissionsPage_SubMissions_QueryDocument,
    { missionId },
    { enabled: !!missionId },
  );

  const newMissionPath = useBuildPath(PATH_COLLABORATION_MISSIONS_NEW, {
    folderId,
    missionId,
  });

  const creationGate = useSubMissionCreationGate(folderId, missionId);

  const goToCreationPage = useCallback(
    () => void navigate(newMissionPath),
    [navigate, newMissionPath],
  );

  if (isLoading) {
    return <Loading />;
  }

  const subMissions = data?.findOneMission.subMissions ?? EMPTY_ARRAY;

  if (!subMissions.length) {
    return (
      <SplashScreen
        action={{
          label: 'Créer une Sous-Mission',
          onClick: goToCreationPage,
          gate: creationGate,
        }}
        illustration={<SaveToBookmarks />}
        message="Cette Collaboration ne regroupe aucune sous-mission pour le moment."
      />
    );
  }

  return (
    <FolderBusinessEntityList
      createGate={creationGate}
      folderBusinessEntities={subMissions}
      newFolderBusinessEntityString="Créer une nouvelle sous-mission"
      onStartCreate={goToCreationPage}
      variant={FolderBusinessEntityListVariant.Cards}
    />
  );
};
